import React, { useContext, SyntheticEvent, FunctionComponent, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

// MUI
import Grid from '@mui/material/Grid';
import Dialog from '@mui/material/Dialog';
import Toolbar from '@mui/material/Toolbar';

// MUI Icons
import TodayIcon from '@mui/icons-material/Today';

// Custom Components
import useFormController from '@/components/useFormController';
import DialogHeader from '@/ui/Dialog/DialogHeader';
import DialogFooter from '@/ui/Dialog/DialogFooter';
import DialogToolbarButtonClose from '@/ui/Dialog/ToolbarButtons/DialogToolbarButtonClose';
import ToolbarFillContent from '@/ui/Toolbar/ToolbarFillContent';
import DialogBody from '@/ui/Dialog/DialogBody';
import DialogActionButtonClose from '@/ui/Dialog/ActionButtons/DialogActionButtonClose';
import DialogActionButtonAdd from '@/ui/Dialog/ActionButtons/DialogActionButtonAdd';
import DialogActionButtonSave from '@/ui/Dialog/ActionButtons/DialogActionButtonSave';
import DialogToolbarMenuButton from '@/ui/Dialog/DialogToolbarMenuButton';
import DialogToolbarMenuItemDelete from '@/ui/Dialog/ToolbarMenuItems/DialogToolbarMenuItemDelete';
import DialogContext from '@/ui/DialogContext/DialogContext';
import SnackbarContext from '@/ui/SnackbarContext/SnackbarContext';
import FormContent from '@/components/FormContent';
import FormAvatar from '@/ui/Form/FormAvatar';
import FormHeading from '@/ui/Form/FormHeading';
import RouteMap from '@/views/GIS/MapPage/RouteMap';
import { RoutePoint } from '@/components/MapSidebarPanes/RoutePaneV2';
import MapRoutingProvider from '@/components/MapRoutingContext/MapRoutingProvider';

// OL
import { LineString } from 'ol/geom';
import OlFeature from 'ol/Feature';
import { transform } from 'ol/proj';

// Types
import { ClosingDetails, FormControllerProps } from '@/@types/components/formController';
import { IDialogProps } from '@/@types/ui/DialogContext';
import { DCFieldValue, DCRecord } from '@/@types/lib/dataController';
import DialogActionButtonCancel from '@/ui/Dialog/ActionButtons/DialogActionButtonCancel';


const noRouteTitle = 'cdialogs.title.no_route';
const noRouteText = 'cdialogs.text.no_route';
const noRouteConfirm = 'cdialogs.confirm';
const noRouteCancel = 'cdialogs.cancel';

const IzletDanDialog: FunctionComponent<IDialogProps> = (props) => {
  const dialogContext = useContext(DialogContext);
  const snackbarContext = useContext(SnackbarContext);
  const { t } = useTranslation();

  const { mode, onClose, dc, variant, excursionIsCroatian } = props;

  const [routePointCount, setRoutePointCount] = useState(0);

  const { record, validation, dataChanged, fields, subModels, onFieldChange, doClose, doDelete, doUpdate, doInsert } = useFormController(
    props as FormControllerProps
  );

  const insertDanRecord = () => {
    doInsert()
      .then((result) => {
        if (result.success) {
          snackbarContext.showNotification(t('notifications.insert'), 'success');
          close({ dataChanged: true, action: 'insert' });
        } else if (result.error) {
          const msg = t('error.addRecord') + result.error;
          snackbarContext.showNotification(msg, 'error');
        } else if (result.validationPass === false) {
          // do nothing
        }
      })
      .catch((resp) => {
        snackbarContext.showNotification(resp.error, 'error');
      });
  }

  const handleInsert = (evt: SyntheticEvent) => {
    if (routePointCount <= 1 && excursionIsCroatian) {
      dialogContext
        .showConfirmDialog({
          title: t(noRouteTitle),
          text: t(noRouteText),
          confirmButtonText: t(noRouteConfirm),
          cancelButtonText: t(noRouteCancel),
        })
        .then((result) => {
          if (!result.canceled && result.confirmed) {
            insertDanRecord();
          }
        });
    } else {
      insertDanRecord();
    }
  };

  const updateDanRecord = () => {
    doUpdate().then((result) => {
      if (result.success) {
        snackbarContext.showNotification(t('notifications.update'), 'success');
        close({ dataChanged: true, action: 'update' });
      } else if (result.error) {
        const msg = t('error.editRecord') + result.error;
        snackbarContext.showNotification(msg, 'error');
      } else if (result.validationPass === false) {
        // do nothing
      }
    });
  }

  const handleUpdate = (evt: SyntheticEvent) => {
    if (routePointCount <= 1 && excursionIsCroatian) {
      dialogContext
        .showConfirmDialog({
          title: t(noRouteTitle),
          text: t(noRouteText),
          confirmButtonText: t(noRouteConfirm),
          cancelButtonText: t(noRouteCancel),
        })
        .then((result) => {
          if (!result.canceled && result.confirmed) {
            updateDanRecord();
          }
        });
    } else {
      updateDanRecord();
    }
  };

  const handleClose = (evt: SyntheticEvent) => {
    doClose()
      .then((result) => {
        if (result.success) {
          close({ dataChanged: false, action: 'cancel' });
        } else {
          if (result.shouldSave) {
            handleUpdate(evt);
          } else if (result.canceled) {
            // do nothing
          }
        }
      })
      .catch((result) => {
        console.log(result);
      });
  };

  const handleDelete = () => {
    doDelete().then((result) => {
      if (result.success) {
        snackbarContext.showNotification(t('notifications.delete'), 'success');
        close({ dataChanged: true, action: 'delete' });
      } else if (result.error) {
        const msg = t('error.delete') + result.error;
        snackbarContext.showNotification(msg, 'error');
      }
    });
  };

  const close = (result: ClosingDetails) => {
    if (result.dataChanged || dataChanged) {
      onClose({ dataChanged: true, action: result.action });
    }

    dialogContext.hideDialog(2);
  };

  const handleRoutePointChange = useCallback((routePoints: RoutePoint[]) => {
    setRoutePointCount(routePoints.length);
  }, []);

  // console.log(record);

  const handleRouteFound = useCallback(
    (routeData: { record: DCRecord; feature: OlFeature<LineString> } | null, routePoints: RoutePoint[] | null) => {
      if (routeData?.record.wkb) {
        onFieldChange(routeData.record.wkb, 'wkb');
      } else {
        onFieldChange(null, 'wkb');
      }

      if (routeData?.record.total_length) onFieldChange(routeData.record.total_length, "total_length");
      if (routeData?.record.total_time_min) onFieldChange(routeData.record.total_time_min, "total_time_min");
      if (routeData?.record.total_ascent) onFieldChange(routeData.record.total_ascent, "total_ascent");
      if (routeData?.record.total_descent) onFieldChange(routeData.record.total_descent, "total_descent");

      if (routePoints) {
        const routePointData = routePoints.map((rp, pt_order) => {
          // Convert coords from EPSG:3857 to EPSG:3765
          const transformedCoords = transform(rp.coord, 'EPSG:3857', 'EPSG:3765');

          // Format as WKT Point
          const wkt = `POINT(${transformedCoords[0]} ${transformedCoords[1]})`;

          const { poi_id } = rp;

          return {
            pt_order,
            poi_id,
            wkt,
          };
        });
        onFieldChange(routePointData as DCFieldValue, 'route_points');
      } else {
        onFieldChange(null, 'route_points');
      }
    },
    []
  );

  const isRecordDanEntered = record?.dan !== undefined && record?.dan !== null && record?.dan !== '';

  const title = isRecordDanEntered ? (`${record.dan}. ${t('titles.izlet_dan')}`) : t('titles.izlet_dan');
  const disableSave = dataChanged ? false : true;

  const allowDelete = mode === 'update'; // TODO: how to fix, maybe pass prop to check?

  const hasToolbarActions = allowDelete;

  const icon = <TodayIcon />;

  const isReadOnly = mode === "view";

  return (
    <Dialog maxWidth={excursionIsCroatian ? false : "sm"} open onClose={handleClose} fullWidth={excursionIsCroatian} 
      sx={{
        minHeight: excursionIsCroatian ? "100vh" : undefined,
        maxHeight: excursionIsCroatian ? "100vh": undefined,
      }}>
      <DialogHeader>
        <Toolbar variant="dense" disableGutters>
          <FormAvatar icon={icon} ariaLabel="opis" />
          <FormHeading header={title} />
          <ToolbarFillContent />
          {hasToolbarActions ? (
            <DialogToolbarMenuButton>
              {mode === 'update' ? <DialogToolbarMenuItemDelete onClick={handleDelete} /> : null}
            </DialogToolbarMenuButton>
          ) : null}

          <DialogToolbarButtonClose onClick={handleClose} />
        </Toolbar>
      </DialogHeader>
      <DialogBody sx={{ pl: 2, pr: 0, overflowY: { xs: 'auto', md: 'hidden' } }}>
        <Grid container spacing={2} p={0}>
          <Grid container size={excursionIsCroatian ? {xs: 12, md: 3} : 12} rowSpacing={0} sx={{pt: 2, pr: excursionIsCroatian ? 0 : 2, height: '100%'}}>
            <FormContent
              fieldNames={dc.getFormFieldsNames(variant)}
              record={record}
              validation={validation}
              fields={fields}
              onFieldChange={onFieldChange}
              mode={mode}
              columns={2}
              subModels={subModels}
            />
          </Grid>
          {excursionIsCroatian ?
            <Grid size={{xs: 12, md: 9}} container sx={{ height: 'calc(100vh - 204px)' }}>
              <MapRoutingProvider>
                <RouteMap
                  onRoutePointChange={handleRoutePointChange}
                  onRouteFoundChange={handleRouteFound}
                  mapId={11}
                  mode={mode}
                  initialRecordRoutePoints={record.route_points as DCRecord[]}
                />
              </MapRoutingProvider>
            </Grid> 
          : null}
        </Grid>
      </DialogBody>
      <DialogFooter>
        {mode === 'view' ? (
          <DialogActionButtonClose variant="outlined" onClick={handleClose} />
        ) : (
          <DialogActionButtonCancel variant="outlined" onClick={handleClose} />
        )}
        {mode === 'insert' ? <DialogActionButtonAdd variant="contained" onClick={handleInsert} /> : null}
        {mode === 'update' ? <DialogActionButtonSave variant="contained" onClick={handleUpdate} disabled={disableSave} /> : null}
      </DialogFooter>
    </Dialog>
  );
};

export default IzletDanDialog;
